import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { riskDisclosureSections } from '../content/riskDisclosure';
import { LanguageSwitch } from '../i18n/LanguageSwitch';
import { LocaleText } from '../i18n/Locale';

export default function RiskDisclosure() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="risk-disclosure-page">
      <header className="border-b border-slate-800">
        <nav aria-label="Risk disclosure navigation" className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-6">
          <Link to="/" data-testid="risk-home-link" className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400">
            <ArrowLeft size={16} aria-hidden="true" />Home
          </Link>
          <p className="text-sm font-semibold text-slate-100" data-testid="risk-brand">Superior Traders Group</p>
          <LanguageSwitch id="risk-language-switch" />
        </nav>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <p className="mb-4 text-xs font-semibold text-green-400" data-testid="risk-page-label">IMPORTANT INFORMATION</p>
        <h1 className="mb-6 text-4xl font-bold sm:text-5xl lg:text-6xl" data-testid="risk-disclosure-title">Risk Disclosure</h1>
        <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base" data-testid="risk-disclosure-introduction">
          Understand the risks before relying on research, following a trader, connecting a wallet, or committing funds.
        </p>
        <div className="mt-8 flex items-start gap-3 border-l-2 border-red-400 py-2 pl-4" role="note" data-testid="risk-loss-warning">
          <TriangleAlert size={20} className="mt-1 shrink-0 text-red-400" aria-hidden="true" />
          <p className="text-sm leading-7 text-slate-200">You may lose all money committed. We do not provide trading or investment advice, and no outcome is guaranteed.</p>
        </div>

        <div className="mt-12 space-y-10" data-testid="risk-disclosure-sections">
          {riskDisclosureSections.map((section, index) => (
            <section key={section.id} aria-labelledby={`risk-${section.id}-heading`} data-testid={`risk-section-${section.id}`}>
              <h2 id={`risk-${section.id}-heading`} className="mb-4 text-base font-semibold text-slate-100 md:text-lg" data-testid={`risk-heading-${section.id}`}>
                <span className="mr-3 text-green-400" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span><LocaleText value={section.title} />
              </h2>
              <div className="space-y-3 text-sm leading-7 text-slate-300 sm:text-base">
                {section.paragraphs.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex} data-testid={`risk-${section.id}-paragraph-${paragraphIndex + 1}`}><LocaleText value={paragraph} /></p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-12 flex flex-wrap items-center gap-4 border-t border-slate-800 pt-8" data-testid="risk-disclosure-footer">
          <Button asChild className="bg-green-600 text-white hover:bg-green-700">
            <Link to="/?signin=1" data-testid="risk-login-link">Login Now<ArrowRight size={16} className="ml-2" aria-hidden="true" /></Link>
          </Button>
          <Link to="/" data-testid="risk-return-home-link" className="text-sm text-slate-300 underline underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400">Return to homepage</Link>
        </footer>
      </main>
    </div>
  );
}